import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Printer, Search, TrendingUp, TrendingDown, BarChart3, Users } from 'lucide-react';
import { getProfitByCustomer, getMonthlyProfit } from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const ProfitByCustomer = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('profit');
  const [showChart, setShowChart] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [result, monthly] = await Promise.all([
        getProfitByCustomer(),
        getMonthlyProfit()
      ]);
      setData(result);
      setMonthlyData(monthly.map(m => ({
        ...m,
        label: formatMonthLabel(m.month)
      })));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMonthLabel = (month) => {
    if (!month) return '';
    const [y, m] = month.split('-');
    const months = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  const fmt = (val) => `₪${(val || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('he-IL');
  };

  const filtered = data
    .filter(c => !searchQuery.trim() || c.customerName.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    .sort((a, b) => sortBy === 'profit' ? b.profit - a.profit : sortBy === 'income' ? b.income - a.income : b.transactions - a.transactions);

  const totals = {
    income: filtered.reduce((s, c) => s + c.income, 0),
    cost: filtered.reduce((s, c) => s + c.cost, 0),
    profit: filtered.reduce((s, c) => s + c.profit, 0)
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען דוח רווח...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4 print:bg-white print:py-2" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center print:hidden">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">דוח רווח לפי לקוח</h1>
            <p className="text-gray-600">{data.length} לקוחות</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => window.print()} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
              <Printer className="ml-2 w-5 h-5" />
              הדפסה
            </Button>
            <Button onClick={() => navigate('/reports')} variant="outline" className="border-2 hover:bg-gray-50">
              <ArrowRight className="ml-2 w-5 h-5" />
              חזור לדווח
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6 print:hidden">
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-green-700 font-semibold">סה&quot;כ רווח</p>
              <p className="text-2xl font-bold text-green-900">{fmt(totals.profit)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-blue-700 font-semibold">סה&quot;כ הכנסות</p>
              <p className="text-2xl font-bold text-blue-900">{fmt(totals.income)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-red-700 font-semibold">סה&quot;כ עלויות</p>
              <p className="text-2xl font-bold text-red-900">{fmt(totals.cost)}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-purple-700 font-semibold">מרווח ממוצע</p>
              <p className="text-2xl font-bold text-purple-900">{totals.income > 0 ? (totals.profit / totals.income * 100).toFixed(1) : 0}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Profit Chart */}
        {monthlyData.length > 0 && (
          <div className="mb-6 print:hidden">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                גרף רווח חודשי
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChart(!showChart)}
                data-testid="toggle-chart-btn"
              >
                {showChart ? 'הסתר גרף' : 'הצג גרף'}
              </Button>
            </div>
            {showChart && (
              <Card className="border-2 border-gray-200 shadow-lg">
                <CardContent className="pt-6 pb-2">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={monthlyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        angle={-35}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          const labels = { income: 'הכנסות', cost: 'עלויות', profit: 'רווח' };
                          return [`₪${value.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`, labels[name] || name];
                        }}
                        labelStyle={{ direction: 'rtl', fontWeight: 'bold' }}
                        contentStyle={{ direction: 'rtl', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const labels = { income: 'הכנסות', cost: 'עלויות', profit: 'רווח' };
                          return labels[value] || value;
                        }}
                      />
                      <Bar dataKey="income" fill="#3b82f6" radius={[4, 4, 0, 0]} name="income" />
                      <Bar dataKey="cost" fill="#ef4444" radius={[4, 4, 0, 0]} name="cost" />
                      <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} name="profit" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Search & Sort */}
        <div className="mb-6 flex gap-4 print:hidden">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="חיפוש לפי שם לקוח..."
              className="w-full pr-10 pl-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors bg-white"
              data-testid="profit-search"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-sm font-semibold"
            data-testid="profit-sort"
          >
            <option value="profit">מיון לפי רווח</option>
            <option value="income">מיון לפי הכנסות</option>
            <option value="transactions">מיון לפי עסקאות</option>
          </select>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-4 text-center">
          <h1 className="text-2xl font-bold">דוח רווח לפי לקוח</h1>
          <p className="text-sm text-gray-600">{new Date().toLocaleDateString('he-IL')}</p>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">אין נתונים להצגה</p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto border rounded-lg shadow-lg bg-white">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-emerald-600 to-green-600 text-white">
                  <th className="p-3 text-right font-bold">#</th>
                  <th className="p-3 text-right font-bold">שם לקוח</th>
                  <th className="p-3 text-right font-bold">הכנסות</th>
                  <th className="p-3 text-right font-bold">עלויות</th>
                  <th className="p-3 text-right font-bold">רווח</th>
                  <th className="p-3 text-center font-bold">מרווח %</th>
                  <th className="p-3 text-center font-bold">עסקאות</th>
                  <th className="p-3 text-right font-bold">תאריך ראשון</th>
                  <th className="p-3 text-right font-bold">תאריך אחרון</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50 transition-colors`}>
                    <td className="p-3 border-b text-gray-500">{idx + 1}</td>
                    <td className="p-3 border-b font-semibold text-gray-900">{c.customerName}</td>
                    <td className="p-3 border-b text-blue-700 font-medium">{fmt(c.income)}</td>
                    <td className="p-3 border-b text-red-600 font-medium">{fmt(c.cost)}</td>
                    <td className="p-3 border-b font-bold">
                      <span className={`flex items-center gap-1 ${c.profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {c.profit >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {fmt(c.profit)}
                      </span>
                    </td>
                    <td className="p-3 border-b text-center">
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${c.margin >= 30 ? 'bg-green-100 text-green-800' : c.margin >= 15 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700'}`}>
                        {c.margin}%
                      </span>
                    </td>
                    <td className="p-3 border-b text-center text-gray-700">{c.transactions}</td>
                    <td className="p-3 border-b text-gray-600 text-xs">{formatDate(c.firstDate)}</td>
                    <td className="p-3 border-b text-gray-600 text-xs">{formatDate(c.lastDate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-bold">
                  <td colSpan={2} className="p-3 text-right border-t-2">סה&quot;כ {filtered.length} לקוחות</td>
                  <td className="p-3 border-t-2 text-blue-700">{fmt(totals.income)}</td>
                  <td className="p-3 border-t-2 text-red-600">{fmt(totals.cost)}</td>
                  <td className="p-3 border-t-2 text-green-700">{fmt(totals.profit)}</td>
                  <td className="p-3 border-t-2 text-center">{totals.income > 0 ? (totals.profit / totals.income * 100).toFixed(1) : 0}%</td>
                  <td colSpan={3} className="p-3 border-t-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitByCustomer;
